import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { KommaLogo } from "@/components/komma/KommaLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDetail } from "@/lib/api";
import { toast } from "sonner";

const DEMO_ACCOUNTS = [
    { label: "CEO", email: "ceo@komma.com" },
    { label: "Office Admin", email: "admin@komma.com" },
    { label: "Branch Manager", email: "manager@komma.com" },
    { label: "Marketing Exec", email: "marketing@komma.com" },
    { label: "Agent", email: "agent@komma.com" },
    { label: "Telecaller", email: "telecaller@komma.com" },
    { label: "Customer", email: "customer@komma.com" },
];

const DEMO_MODE = String(process.env.REACT_APP_DEMO_MODE || "").toLowerCase() === "true";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
            toast.success("Welcome back to Komma Associates");
            nav("/");
        } catch (err) {
            toast.error(formatDetail(err.response?.data?.detail) || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    const quickFill = (em) => {
        setEmail(em);
        setPassword("Komma@2026");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
            {/* Left: Form */}
            <div className="flex flex-col justify-center px-8 md:px-16 py-12 bg-komma-cream relative">
                <div className="max-w-md w-full mx-auto">
                    <div className="flex items-center gap-3 mb-10">
                        <KommaLogo size={64} />
                        <div>
                            <p className="font-serif text-xl text-komma-navy leading-none">Komma</p>
                            <p className="text-[10px] uppercase tracking-[0.32em] text-komma-gold mt-1">Associates</p>
                        </div>
                    </div>

                    <h1 className="font-serif text-4xl text-komma-navy leading-tight" data-testid="login-heading">Welcome to your<br />financial console.</h1>
                    <p className="mt-3 text-sm text-gray-600">Sign in to manage loans, customers, and document verification with confidence.</p>

                    <form onSubmit={submit} className="mt-8 space-y-5">
                        <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-komma-navy">Email</Label>
                            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email"
                                className="mt-2 h-11 border-gray-300 focus-visible:ring-komma-gold" placeholder="you@komma.com" />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-komma-navy">Password</Label>
                            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password"
                                className="mt-2 h-11 border-gray-300 focus-visible:ring-komma-gold" placeholder="••••••••" />
                        </div>
                        <Button type="submit" disabled={busy} data-testid="login-submit"
                            className="w-full h-11 bg-komma-navy hover:bg-komma-navy-light text-white text-sm font-semibold tracking-wide rounded-md">
                            {busy ? "Signing in…" : "Sign In"}
                        </Button>
                    </form>

                    <div className="mt-8">
                        {DEMO_MODE ? (
                            <>
                                <p className="text-[10px] uppercase tracking-[0.28em] text-gray-500 mb-3">Demo accounts (password: Komma@2026)</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {DEMO_ACCOUNTS.map((a) => (
                                        <button key={a.email} type="button"
                                            data-testid={`demo-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                                            onClick={() => quickFill(a.email)}
                                            className="text-left px-3 py-2 rounded-md border border-gray-200 hover:border-komma-gold hover:bg-white transition-colors">
                                            <span className="block text-[10px] uppercase tracking-wider text-komma-gold">{a.label}</span>
                                            <span className="block text-xs text-komma-navy truncate">{a.email}</span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p className="text-[10px] uppercase tracking-[0.28em] text-gray-400">
                                Contact your CEO or Office Admin for account access
                            </p>
                        )}
                    </div>

                    <p className="mt-8 text-xs text-gray-500">
                        Trouble signing in? Ask your CEO to reset your password.
                    </p>
                </div>
            </div>

            {/* Right: Brand panel */}
            <div className="hidden lg:flex relative overflow-hidden bg-komma-navy komma-noise">
                <img
                    src="https://images.unsplash.com/photo-1576731753569-3e93a228048c?crop=entropy&cs=srgb&fm=jpg&w=1400&q=70"
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover opacity-30"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-komma-navy-dark via-komma-navy/90 to-komma-navy-light/80" />
                <div className="relative z-10 flex flex-col justify-between p-16 text-white w-full">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/95 rounded-xl p-3">
                            <KommaLogo size={72} />
                        </div>
                        <div>
                            <p className="font-serif text-2xl">Komma Associates</p>
                            <p className="text-[10px] uppercase tracking-[0.32em] text-komma-gold mt-1">Trust · Growth · Prosperity</p>
                        </div>
                    </div>
                    <div className="max-w-md">
                        <p className="text-[11px] uppercase tracking-[0.32em] text-komma-gold">Premium Financial Services</p>
                        <h2 className="font-serif text-4xl mt-3 leading-tight">Empowering aspirations<br />with lending you can trust.</h2>
                        <p className="mt-4 text-white/70 text-sm leading-relaxed">
                            One console for CEO analytics, office operations, marketing pipelines, field agents, telecalling, and customer self-service — all secured, audited, and beautiful.
                        </p>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-white/40">© 2026 Komma Associates</div>
                </div>
            </div>
        </div>
    );
}
